import React,{useCallback,useEffect,useRef,useState}from"react";
import{Autocomplete,Box,Button,Checkbox,Dialog,DialogActions,DialogContent,DialogTitle,FormControlLabel,TextField,Typography}from"@mui/material";
import{useNavigate}from"react-router-dom";
import{useAuth}from"../../../context/AuthContext";
import{useAlert}from"../../../context/AlertContext";

const emptyForm={
  equipment:"",
  brand:"",
  equipment_model:"",
  work_area:"",
  equipment_code:"",
  amperage:"",
  low_psi:"",
  high_psi:"",
  voltage:"",
  temperature:"",
  refrigerant_lb:"",
  preventive_maintenance:false,
  corrective_maintenance:false,
  technical_assistance:false,
  installation:false,
  antecedentes:"",
  trabajo_realizado:"",
  materiales:"",
};

export default function OrderDetail({contactSlug,publicId}){
  const navigate=useNavigate();
  const{authFetch,user}=useAuth();
  const{showSuccess,showError,showWarning}=useAlert();

  const[order,setOrder]=useState(null);
  const[form,setForm]=useState(emptyForm);
  const[loading,setLoading]=useState(true);
  const[saving,setSaving]=useState(false);
  const[actionLoading,setActionLoading]=useState(false);
  const[downloading,setDownloading]=useState(false);
  const[supervisors,setSupervisors]=useState([]);
  const[selectedSupervisor,setSelectedSupervisor]=useState(null);
  const[supervisorSearch,setSupervisorSearch]=useState("");
  const[loadingSupervisors,setLoadingSupervisors]=useState(false);
  const[newImages,setNewImages]=useState([]);
  const[rejectOpen,setRejectOpen]=useState(false);
  const[rejectionReason,setRejectionReason]=useState("");
  const fileInputRef=useRef(null);

  const isTechnician=user?.role==="TECHNICIAN";
  const isSupervisor=user?.role==="SUPERVISOR";
  const isAdmin=user?.role==="ADMIN";
  const canEdit=isTechnician&&order?.status==="GENERADA";

  const setOrderData=current=>{
    setOrder(current);
    setForm({
      equipment:current.equipment||"",
      brand:current.brand||"",
      equipment_model:current.equipment_model||"",
      work_area:current.work_area||"",
      equipment_code:current.equipment_code||"",
      amperage:current.amperage||"",
      low_psi:current.low_psi||"",
      high_psi:current.high_psi||"",
      voltage:current.voltage||"",
      temperature:current.temperature||"",
      refrigerant_lb:current.refrigerant_lb||"",
      preventive_maintenance:Boolean(current.preventive_maintenance),
      corrective_maintenance:Boolean(current.corrective_maintenance),
      technical_assistance:Boolean(current.technical_assistance),
      installation:Boolean(current.installation),
      antecedentes:current.antecedentes||"",
      trabajo_realizado:current.trabajo_realizado||"",
      materiales:current.materiales||"",
    });
    setSelectedSupervisor(current.supervisor||null);
    setSupervisorSearch(current.supervisor?.name||"");
  };

  const loadOrder=useCallback(async()=>{
    setLoading(true);
    try{
      const response=await authFetch(`/api/orden/${contactSlug}/${publicId}/`);
      const data=await response.json();
      if(!response.ok)throw new Error(data.detail||"No se pudo cargar la orden.");
      setOrderData(data.order||data);
    }catch(error){
      showError(error.message||"No se pudo cargar la orden.");
    }finally{
      setLoading(false);
    }
  },[authFetch,contactSlug,publicId,showError]);

  useEffect(()=>{loadOrder();},[loadOrder]);

  useEffect(()=>{
    if(!canEdit)return;

    const timer=setTimeout(async()=>{
      setLoadingSupervisors(true);

      try{
        const response=await authFetch(`/api/orden/supervisores/?q=${encodeURIComponent(supervisorSearch.trim())}`);
        const data=await response.json();

        if(!response.ok)throw new Error(data.detail||"No se pudieron cargar los contactos.");

        const items=Array.isArray(data)?data:Array.isArray(data.supervisors)?data.supervisors:[];

        if(selectedSupervisor&&!items.some(item=>item.public_id===selectedSupervisor.public_id)){
          setSupervisors([selectedSupervisor,...items]);
        }else{
          setSupervisors(items);
        }
      }catch{
        setSupervisors(selectedSupervisor?[selectedSupervisor]:[]);
      }finally{
        setLoadingSupervisors(false);
      }
    },300);

    return()=>clearTimeout(timer);
  },[supervisorSearch,authFetch,canEdit,selectedSupervisor]);

  const getApiPath=current=>{
    const item=current||order;
    if(item)return `/api/orden/${item.contact_slug}/${item.public_id}/`;
    return `/api/orden/${contactSlug}/${publicId}/`;
  };

  const synchronizeUrl=updated=>{
    if(updated.contact_slug!==contactSlug||updated.public_id!==publicId){
      navigate(`/admin-panel/orders/${updated.contact_slug}/${updated.public_id}`,{replace:true});
    }
  };

  const change=e=>{
    const{name,value}=e.target;
    setForm(prev=>({...prev,[name]:value}));
  };

  const changeCheck=e=>{
    const{name,checked}=e.target;
    setForm(prev=>({...prev,[name]:checked}));
  };

  const addImages=e=>{
    const files=Array.from(e.target.files||[]);
    if(files.length)setNewImages(prev=>[...prev,...files]);
    e.target.value="";
  };

  const removeNewImage=index=>{
    setNewImages(prev=>prev.filter((_,i)=>i!==index));
  };

  const saveOrder=async(showMessage=true)=>{
    if(!selectedSupervisor?.public_id){
      showWarning("Seleccione un contacto.");
      return null;
    }

    setSaving(true);

    try{
      const body=new FormData();
      body.append("supervisor",selectedSupervisor.public_id);

      Object.entries(form).forEach(([key,value])=>{
        body.append(key,value);
      });

      newImages.forEach(image=>body.append("images",image));

      const response=await authFetch(getApiPath(),{
        method:"PATCH",
        body,
      });

      const data=await response.json();

      if(!response.ok){
        if(data.errors)throw new Error(Object.values(data.errors).flat().join(" "));
        throw new Error(data.detail||"No se pudo guardar la orden.");
      }

      const updated=data.order||data;
      setOrderData(updated);
      setNewImages([]);
      synchronizeUrl(updated);

      if(showMessage)showSuccess("Orden actualizada correctamente.");

      return updated;
    }catch(error){
      showError(error.message||"No se pudo guardar la orden.");
      return null;
    }finally{
      setSaving(false);
    }
  };

  const deleteImage=async imageId=>{
    try{
      const response=await authFetch(`${getApiPath()}imagenes/${imageId}/`,{
        method:"DELETE",
      });

      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudo eliminar la imagen.");

      setOrder(prev=>({
        ...prev,
        images:(prev.images||[]).filter(image=>image.id!==imageId),
      }));

      showSuccess("Imagen eliminada correctamente.");
    }catch(error){
      showError(error.message||"No se pudo eliminar la imagen.");
    }
  };

  const sendForReview=async()=>{
    const savedOrder=await saveOrder(false);
    if(!savedOrder)return;

    setActionLoading(true);

    try{
      const response=await authFetch(`${getApiPath(savedOrder)}enviar-revision/`,{
        method:"POST",
      });

      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudo enviar la orden a revisión.");

      const updated=data.order||data;
      setOrderData(updated);
      synchronizeUrl(updated);

      if(data.email_sent===false&&data.email_error){
        showWarning(`Orden enviada a revisión, pero no se pudo enviar el correo: ${data.email_error}`);
      }else{
        showSuccess("Orden enviada a revisión correctamente.");
      }
    }catch(error){
      showError(error.message||"No se pudo enviar la orden a revisión.");
    }finally{
      setActionLoading(false);
    }
  };

  const approveOrder=async()=>{
    setActionLoading(true);

    try{
      const response=await authFetch(`${getApiPath()}aprobar/`,{method:"POST"});
      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudo aprobar la orden.");

      const updated=data.order||data;
      setOrderData(updated);
      synchronizeUrl(updated);
      showSuccess("Orden aprobada correctamente.");
    }catch(error){
      showError(error.message||"No se pudo aprobar la orden.");
    }finally{
      setActionLoading(false);
    }
  };

  const rejectOrder=async()=>{
    if(!rejectionReason.trim()){
      showWarning("Ingrese el motivo del rechazo.");
      return;
    }

    setActionLoading(true);

    try{
      const response=await authFetch(`${getApiPath()}rechazar/`,{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({reason:rejectionReason.trim()}),
      });

      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudo rechazar la orden.");

      const updated=data.order||data;
      setOrderData(updated);
      synchronizeUrl(updated);
      setRejectOpen(false);
      setRejectionReason("");
      showSuccess("Orden rechazada correctamente.");
    }catch(error){
      showError(error.message||"No se pudo rechazar la orden.");
    }finally{
      setActionLoading(false);
    }
  };

  const reopenOrder=async()=>{
    setActionLoading(true);

    try{
      const response=await authFetch(`${getApiPath()}reabrir/`,{method:"POST"});
      const data=await response.json();

      if(!response.ok)throw new Error(data.detail||"No se pudo habilitar la orden.");

      const updated=data.order||data;
      setOrderData(updated);
      synchronizeUrl(updated);
      showSuccess("Orden habilitada nuevamente para edición.");
    }catch(error){
      showError(error.message||"No se pudo habilitar la orden.");
    }finally{
      setActionLoading(false);
    }
  };

  const downloadOrder=async()=>{
    setDownloading(true);

    try{
      const response=await authFetch(`${getApiPath()}pdf/`);

      if(!response.ok){
        let message="No se pudo descargar la orden.";

        try{
          const data=await response.json();
          message=data.detail||message;
        }catch{}

        throw new Error(message);
      }

      const blob=await response.blob();
      const url=window.URL.createObjectURL(blob);
      const link=document.createElement("a");

      link.href=url;
      link.download=`orden_${order.number}.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }catch(error){
      showError(error.message||"No se pudo descargar la orden.");
    }finally{
      setDownloading(false);
    }
  };

  const statusColor=status=>{
    if(status==="APROBADA")return"#027A48";
    if(status==="RECHAZADA")return"#B42318";
    if(status==="EN_REVISION")return"#B54708";
    return"#344054";
  };

  const formatDate=value=>{
    if(!value)return"—";
    return new Date(value).toLocaleDateString("es-EC",{day:"2-digit",month:"2-digit",year:"numeric"});
  };

  const fieldSx={"& .MuiOutlinedInput-root":{borderRadius:2,bgcolor:"#fff"}};
  const sectionTitle={fontSize:15,fontWeight:700,color:"#182230",mb:1.5};

  if(loading)return(
    <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,p:5,textAlign:"center"}}>
      <Typography sx={{fontSize:14,color:"#667085"}}>Cargando orden...</Typography>
    </Box>
  );

  if(!order)return null;

  return(
    <Box>
      <Box sx={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:2,mb:2.5,flexWrap:"wrap"}}>
        <Box>
          <Typography sx={{fontSize:24,fontWeight:700,color:"#182230"}}>Orden {order.number}</Typography>

          <Box sx={{display:"flex",alignItems:"center",gap:1.5,mt:.5,flexWrap:"wrap"}}>
            <Typography sx={{fontSize:13,color:"#667085"}}>{formatDate(order.created_at)}</Typography>

            <Typography sx={{fontSize:13,fontWeight:700,color:statusColor(order.status)}}>
              {order.status_display||order.status}
            </Typography>
          </Box>
        </Box>

        <Box sx={{display:"flex",gap:1,flexWrap:"wrap"}}>
          <Button
            variant="outlined"
            onClick={downloadOrder}
            disabled={downloading}
            sx={{borderRadius:2,textTransform:"none",fontWeight:700,color:"#123f6d",borderColor:"#b8c8d8"}}
          >
            {downloading?"Descargando...":"Descargar PDF"}
          </Button>

          {canEdit&&(
            <>
              <Button
                variant="outlined"
                onClick={()=>saveOrder(true)}
                disabled={saving||actionLoading}
                sx={{borderRadius:2,textTransform:"none",fontWeight:700,color:"#123f6d",borderColor:"#b8c8d8"}}
              >
                {saving?"Guardando...":"Guardar cambios"}
              </Button>

              <Button
                variant="contained"
                onClick={sendForReview}
                disabled={saving||actionLoading}
                sx={{bgcolor:"#123f6d",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none","&:hover":{bgcolor:"#0e345b",boxShadow:"none"}}}
              >
                {actionLoading?"Procesando...":"Enviar a revisión"}
              </Button>
            </>
          )}

          {isSupervisor&&order.status==="EN_REVISION"&&(
            <>
              <Button
                variant="outlined"
                onClick={()=>setRejectOpen(true)}
                disabled={actionLoading}
                sx={{borderRadius:2,textTransform:"none",fontWeight:700,color:"#B42318",borderColor:"#FDA29B"}}
              >
                Rechazar
              </Button>

              <Button
                variant="contained"
                onClick={approveOrder}
                disabled={actionLoading}
                sx={{bgcolor:"#027A48",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none","&:hover":{bgcolor:"#05603A",boxShadow:"none"}}}
              >
                {actionLoading?"Procesando...":"Aprobar"}
              </Button>
            </>
          )}

          {isAdmin&&order.status==="RECHAZADA"&&(
            <Button
              variant="contained"
              onClick={reopenOrder}
              disabled={actionLoading}
              sx={{bgcolor:"#123f6d",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none","&:hover":{bgcolor:"#0e345b",boxShadow:"none"}}}
            >
              {actionLoading?"Procesando...":"Habilitar edición"}
            </Button>
          )}
        </Box>
      </Box>

      {order.status==="RECHAZADA"&&order.rejection_reason&&(
        <Box sx={{bgcolor:"#fff7f6",border:"1px solid #fecdca",borderRadius:2,p:2,mb:2.5}}>
          <Typography sx={{fontSize:13,fontWeight:700,color:"#B42318",mb:.5}}>Motivo del rechazo</Typography>
          <Typography sx={{fontSize:13.5,color:"#344054",whiteSpace:"pre-line"}}>{order.rejection_reason}</Typography>
        </Box>
      )}

      <Box sx={{bgcolor:"#fff",border:"1px solid #dce3ea",borderRadius:3,p:{xs:2,md:3},maxWidth:1100}}>
        <Typography sx={sectionTitle}>Información de la orden</Typography>

        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"1fr 1fr"},gap:2}}>
          <Box>
            <Typography sx={{fontSize:12,color:"#667085"}}>Técnico</Typography>
            <Typography translate="no" className="notranslate" sx={{fontSize:13.5,fontWeight:600,color:"#344054"}}>
              {order.technician?.name||"—"}
            </Typography>
          </Box>

          {!canEdit&&(
            <Box>
              <Typography sx={{fontSize:12,color:"#667085"}}>Contacto</Typography>
              <Typography translate="no" className="notranslate" sx={{fontSize:13.5,fontWeight:600,color:"#344054"}}>
                {order.supervisor?.name||"—"}
              </Typography>
            </Box>
          )}
        </Box>

        {canEdit&&(
          <Box sx={{mt:2}}>
            <Autocomplete
              value={selectedSupervisor}
              options={supervisors}
              loading={loadingSupervisors}
              filterOptions={options=>options}
              getOptionLabel={option=>option?.name||""}
              isOptionEqualToValue={(option,value)=>option.public_id===value.public_id}
              onChange={(event,value)=>setSelectedSupervisor(value)}
              onInputChange={(event,value,reason)=>{
                if(reason==="input")setSupervisorSearch(value);
                if(reason==="clear")setSupervisorSearch("");
              }}
              noOptionsText="No se encontraron contactos"
              loadingText="Buscando contactos..."
              renderInput={params=>(
                <TextField {...params} label="Contacto" size="small" fullWidth sx={fieldSx}/>
              )}
            />
          </Box>
        )}

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Datos del equipo</Typography>

        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",md:"1fr 1fr"},gap:2}}>
          <TextField disabled={!canEdit} label="Equipo" name="equipment" value={form.equipment} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Marca" name="brand" value={form.brand} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Modelo" name="equipment_model" value={form.equipment_model} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Área de trabajo" name="work_area" value={form.work_area} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Código" name="equipment_code" value={form.equipment_code} onChange={change} size="small" fullWidth sx={fieldSx}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Datos técnicos</Typography>

        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr",md:"1fr 1fr 1fr"},gap:2}}>
          <TextField disabled={!canEdit} label="Amperaje" name="amperage" value={form.amperage} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Baja PSI" name="low_psi" value={form.low_psi} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Alta PSI" name="high_psi" value={form.high_psi} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Voltaje" name="voltage" value={form.voltage} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Temperatura" name="temperature" value={form.temperature} onChange={change} size="small" fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Refrigerante LB" name="refrigerant_lb" value={form.refrigerant_lb} onChange={change} size="small" fullWidth sx={fieldSx}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Tipo de servicio</Typography>

        <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},gap:.5}}>
          <FormControlLabel disabled={!canEdit} control={<Checkbox size="small" name="preventive_maintenance" checked={form.preventive_maintenance} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Mantenimiento preventivo</Typography>}/>
          <FormControlLabel disabled={!canEdit} control={<Checkbox size="small" name="corrective_maintenance" checked={form.corrective_maintenance} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Mantenimiento correctivo</Typography>}/>
          <FormControlLabel disabled={!canEdit} control={<Checkbox size="small" name="technical_assistance" checked={form.technical_assistance} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Asistencia técnica</Typography>}/>
          <FormControlLabel disabled={!canEdit} control={<Checkbox size="small" name="installation" checked={form.installation} onChange={changeCheck}/>} label={<Typography sx={{fontSize:13.5}}>Instalación</Typography>}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Información del trabajo</Typography>

        <Box sx={{display:"grid",gap:2}}>
          <TextField disabled={!canEdit} label="Antecedentes" name="antecedentes" value={form.antecedentes} onChange={change} multiline minRows={4} fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Trabajo realizado" name="trabajo_realizado" value={form.trabajo_realizado} onChange={change} multiline minRows={5} fullWidth sx={fieldSx}/>
          <TextField disabled={!canEdit} label="Materiales" name="materiales" value={form.materiales} onChange={change} multiline minRows={4} fullWidth sx={fieldSx}/>
        </Box>

        <Box sx={{height:1,bgcolor:"#edf1f5",my:3}}/>

        <Typography sx={sectionTitle}>Evidencia fotográfica</Typography>

        {order.images?.length>0&&(
          <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr",md:"1fr 1fr 1fr"},gap:1.5,mb:2}}>
            {order.images.map(image=>(
              <Box key={image.id} sx={{border:"1px solid #dce3ea",borderRadius:2,overflow:"hidden"}}>
                <Box component="img" src={image.url} alt="" sx={{width:"100%",height:180,objectFit:"cover",display:"block"}}/>

                {canEdit&&(
                  <Box sx={{p:1,textAlign:"right"}}>
                    <Button size="small" onClick={()=>deleteImage(image.id)} sx={{color:"#B42318",textTransform:"none",fontWeight:700,fontSize:12}}>
                      Quitar
                    </Button>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {!order.images?.length&&newImages.length===0&&(
          <Typography sx={{fontSize:13,color:"#667085",mb:2}}>No hay imágenes registradas.</Typography>
        )}

        {canEdit&&(
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,.heic,.heif,image/jpeg,image/png,image/webp,image/heic,image/heif"
              onChange={addImages}
              style={{display:"none"}}
            />

            <Button
              variant="outlined"
              onClick={()=>fileInputRef.current?.click()}
              sx={{borderRadius:2,textTransform:"none",fontWeight:700,color:"#123f6d",borderColor:"#b8c8d8"}}
            >
              Añadir imágenes
            </Button>

            {newImages.length>0&&(
              <Box sx={{display:"grid",gridTemplateColumns:{xs:"1fr",sm:"1fr 1fr"},gap:1,mt:2}}>
                {newImages.map((image,index)=>(
                  <Box key={`${image.name}-${index}`} sx={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:1,px:1.5,py:1,border:"1px solid #dce3ea",borderRadius:2}}>
                    <Box sx={{minWidth:0}}>
                      <Typography sx={{fontSize:12.5,color:"#344054",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{image.name}</Typography>
                      <Typography sx={{fontSize:11,color:"#98A2B3"}}>{(image.size/1024/1024).toFixed(2)} MB</Typography>
                    </Box>

                    <Button size="small" onClick={()=>removeNewImage(index)} sx={{minWidth:"auto",color:"#B42318",textTransform:"none",fontSize:12,fontWeight:700}}>
                      Quitar
                    </Button>
                  </Box>
                ))}
              </Box>
            )}
          </>
        )}
      </Box>

      <Dialog open={rejectOpen} onClose={()=>!actionLoading&&setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{fontSize:18,fontWeight:700,color:"#182230"}}>Rechazar orden</DialogTitle>

        <DialogContent>
          <Typography sx={{fontSize:13,color:"#667085",mb:2}}>
            Indique el motivo por el que la orden debe regresar para corrección.
          </Typography>

          <TextField
            label="Motivo del rechazo"
            value={rejectionReason}
            onChange={e=>setRejectionReason(e.target.value)}
            multiline
            minRows={4}
            fullWidth
            size="small"
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions sx={{px:3,pb:2.5}}>
          <Button onClick={()=>setRejectOpen(false)} disabled={actionLoading} sx={{textTransform:"none",fontWeight:700,color:"#667085",borderRadius:2}}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={rejectOrder}
            disabled={actionLoading}
            sx={{bgcolor:"#B42318",borderRadius:2,textTransform:"none",fontWeight:700,boxShadow:"none","&:hover":{bgcolor:"#912018",boxShadow:"none"}}}
          >
            {actionLoading?"Procesando...":"Rechazar orden"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}